import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  IconLightning,
  IconPaperplane,
  IconSchedule,
  IconDoneAll,
  IconCheckCircle,
  IconLog,
  IconClose,
  IconVerified,
  IconLoading,
  IconInfo,
  IconContentCopy,
  IconCheck,
  IconOpenInNew,
  IconTrash,
  IconLink,
} from '@components/icons';
import { SocialIcon } from '@components/SocialIcon';
import { IconButton } from '@components/IconButton';
import { Avatar } from '@components/Avatar';
import { getGravatarUrl } from '@hooks/useGravatarUrl';
import { MenuSelect } from '@components/MenuSelect';
import { Button } from '@components/Button';
import { Tooltip } from '@components/Tooltip';
import type { ApolloSequence } from '@services/firebase/firestore';
import styles from './ProfileCard.module.scss';

/** Extract initials from a name (first char of first + last word) */
const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || !parts[0]) return '?';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
};

/** Sequence log entry for contact status tracking */
export interface SequenceLogEntry {
  sendTime: string;
  status: string;
  subject: string;
}

/** Contact status type */
export type ContactStatusType = 'in-progress' | 'completed' | 'successful';

export interface ProfileCardProps {
  /** Person's name (masked if not enriched) */
  name: string;
  /** Job title or role */
  jobTitle?: string;
  /** Email address (shown when enriched) */
  email?: string;
  /** Custom avatar element (image or icon) */
  avatar?: React.ReactNode;
  /** Avatar image URL */
  avatarSrc?: string;
  /** Whether the profile has been enriched */
  enriched?: boolean;
  /** Whether contact has been initiated */
  contacted?: boolean;
  /** Unmasked name shown when enriched and contacted */
  enrichedName?: string;
  /** Whether profile has LinkedIn */
  hasLinkedIn?: boolean;
  /** LinkedIn URL */
  linkedInUrl?: string;
  /** Whether email is verified */
  emailVerified?: boolean;
  /** Contact progress (current/total) */
  contactProgress?: { current: number; total: number };
  /** Contact status type */
  contactStatus?: ContactStatusType;
  /** Sequence log entries for popover */
  sequenceLog?: SequenceLogEntry[];
  /** Person ID (for Apollo contact mapping) */
  personId?: string;
  /** Contact ID from person_id_to_contact_id map (undefined=not added, null=adding, string=added) */
  contactId?: string | null;
  /** Called when Enrich button is clicked */
  onEnrich?: () => void;
  /** Called when Add to Contacts button is clicked */
  onAddToContacts?: () => void;
  /** Called when Reach Out button is clicked (legacy, no sequence) */
  onReachOut?: () => void;
  /** Available Apollo sequences for the reach-out popover */
  sequences?: ApolloSequence[];
  /** Called when user confirms a sequence selection in the popover */
  onReachOutWithSequence?: (sequenceId: string) => void;
  /** Whether a reach-out is currently being processed */
  reachOutPending?: boolean;
  /** Result of the reach-out attempt */
  reachOutResult?: 'success' | 'failed' | 'skipped' | null;
  /** Error/skip message from backend */
  reachOutMessage?: string;
  /** Called when Contact Status is clicked */
  onStatusClick?: () => void;
  /** Called when LinkedIn button is clicked */
  onLinkedInClick?: () => void;
  /** Called when info icon is clicked to view enrichment JSON */
  onViewJson?: () => void;
  /** Called when delete button is clicked (for manually added contacts not yet in Apollo) */
  onDeleteContact?: () => void;
  /**
   * Personalised demo-transfer URL for this contact. When provided AND the
   * card is enriched (i.e. email + first name are available), a "Copy
   * transfer link" icon button is rendered in the actions row. Clicking it
   * copies the URL to clipboard with a 1.5 s checkmark feedback. The parent
   * builds the URL via buildTransferLinkUrl() and only passes a value when
   * both gating conditions hold.
   */
  transferLinkUrl?: string;
  /** Additional CSS classes */
  className?: string;
}

export const ProfileCard: React.FC<ProfileCardProps> = ({
  name,
  jobTitle,
  email,
  avatar,
  avatarSrc,
  enriched = false,
  contacted = false,
  enrichedName,
  hasLinkedIn = false,
  linkedInUrl,
  emailVerified = false,
  contactProgress,
  contactStatus = 'in-progress',
  sequenceLog = [],
  personId: _personId,
  contactId,
  onEnrich,
  onAddToContacts,
  onReachOut,
  sequences,
  onReachOutWithSequence,
  reachOutPending = false,
  reachOutResult = null,
  reachOutMessage,
  onStatusClick,
  onLinkedInClick,
  onViewJson,
  onDeleteContact,
  transferLinkUrl,
  className = '',
}) => {
  const { t } = useTranslation();
  const [showPopover, setShowPopover] = useState(false);
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showSequencePopover, setShowSequencePopover] = useState(false);
  const [selectedSequence, setSelectedSequence] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const statusButtonRef = useRef<HTMLButtonElement>(null);
  const sequencePopoverRef = useRef<HTMLDivElement>(null);
  const reachOutButtonRef = useRef<HTMLButtonElement>(null);
  const [seqPopoverPlacement, setSeqPopoverPlacement] = useState<'above' | 'below'>('above');
  const [popoverCoords, setPopoverCoords] = useState<{ top: number; left: number } | null>(null);

  // Position the sequence log popover below the status button via portal
  const updatePopoverCoords = useCallback(() => {
    if (!statusButtonRef.current) return;
    const rect = statusButtonRef.current.getBoundingClientRect();
    setPopoverCoords({
      top: rect.bottom + 8,
      left: rect.right,
    });
  }, []);

  useLayoutEffect(() => {
    if (showPopover) {
      updatePopoverCoords();
    }
  }, [showPopover, updatePopoverCoords]);

  // Try Gravatar when there's no photo_url but there is an email
  const gravatarUrl = !avatarSrc ? getGravatarUrl(email) : null;

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        statusButtonRef.current &&
        !statusButtonRef.current.contains(event.target as Node)
      ) {
        setShowPopover(false);
      }
    };

    if (showPopover) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPopover]);

  // Close sequence popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sequencePopoverRef.current &&
        !sequencePopoverRef.current.contains(event.target as Node) &&
        reachOutButtonRef.current &&
        !reachOutButtonRef.current.contains(event.target as Node)
      ) {
        setShowSequencePopover(false);
        setSelectedSequence(null);
      }
    };

    if (showSequencePopover) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSequencePopover]);

  const hasSequenceFlow = !!(sequences?.length && onReachOutWithSequence);

  const handleReachOutClick = () => {
    if (hasSequenceFlow) {
      // Determine placement: show above if enough space, otherwise below
      if (reachOutButtonRef.current) {
        const btn = reachOutButtonRef.current;
        const scrollParent = btn.closest('[class*="profilesColumn"]') as HTMLElement | null;
        if (scrollParent) {
          const btnRect = btn.getBoundingClientRect();
          const scrollRect = scrollParent.getBoundingClientRect();
          const spaceAbove = btnRect.top - scrollRect.top;
          // ~320px estimated popover height (280px content + padding + gap)
          setSeqPopoverPlacement(spaceAbove >= 320 ? 'above' : 'below');
        }
      }
      setShowSequencePopover(true);
    } else {
      onReachOut?.();
    }
  };

  const handleSequenceConfirm = () => {
    if (selectedSequence && onReachOutWithSequence) {
      onReachOutWithSequence(selectedSequence);
      setShowSequencePopover(false);
      setSelectedSequence(null);
    }
  };

  // Determine display name based on state
  const displayName = contacted && enrichedName ? enrichedName : name;

  // Determine card variant class
  const cardClassName = [
    styles.container,
    contacted ? styles.contacted : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Get status icon and label
  const getStatusIcon = () => {
    switch (contactStatus) {
      case 'completed':
        return <IconDoneAll size={20} color="var(--color-text-tertiary)" />;
      case 'successful':
        return <IconCheckCircle size={20} color="var(--color-semantic-success)" />;
      case 'in-progress':
      default:
        return <IconSchedule size={20} color="var(--color-semantic-info)" />;
    }
  };

  const getStatusLabel = () => {
    if (!contactProgress) return '';
    const { current, total } = contactProgress;

    switch (contactStatus) {
      case 'completed':
        return 'Completed';
      case 'successful':
        return 'Successful';
      case 'in-progress':
      default:
        return current > 0 ? `In Progress (${current}/${total})` : 'In Progress';
    }
  };

  const getStatusColor = () => {
    switch (contactStatus) {
      case 'completed':
        return 'var(--color-text-tertiary)';
      case 'successful':
        return 'var(--color-semantic-success)';
      case 'in-progress':
      default:
        return 'var(--color-semantic-info)';
    }
  };

  const handleStatusClick = () => {
    setShowPopover(!showPopover);
    onStatusClick?.();
  };

  const handleCopyEmail = () => {
    if (email) {
      navigator.clipboard.writeText(email).then(
        () => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        },
        () => {
          // Clipboard API unavailable (e.g. insecure context) — silently ignore
        }
      );
    }
  };

  const handleCopyTransferLink = () => {
    if (transferLinkUrl) {
      navigator.clipboard.writeText(transferLinkUrl).then(
        () => {
          setLinkCopied(true);
          setTimeout(() => setLinkCopied(false), 1500);
        },
        () => {
          // Clipboard API unavailable (e.g. insecure context) — silently ignore
        }
      );
    }
  };

  const handleLinkedInClick = () => {
    if (linkedInUrl) {
      window.open(linkedInUrl, '_blank', 'noopener,noreferrer');
    }
    onLinkedInClick?.();
  };

  // Render avatar: photo_url → Gravatar (with initials fallback on 404) → initials → icon
  const initials = getInitials(displayName);
  const renderAvatar = () => {
    if (avatar) return avatar;
    if (avatarSrc) return <Avatar src={avatarSrc} alt={displayName} fallbackInitials={initials} />;
    if (gravatarUrl) return <Avatar src={gravatarUrl} alt={displayName} fallbackInitials={initials} />;
    if (initials !== '?') return <Avatar initials={initials} alt={displayName} />;
    return <Avatar />;
  };

  return (
    <div className={cardClassName}>
      {/* Contact Info Section */}
      <div className={styles.contactInfo}>
        <div className={styles.avatar}>{renderAvatar()}</div>
        <div className={styles.infoWrapper}>
          <p className={styles.name}>{displayName}</p>
          {jobTitle && <p className={styles.jobTitle}>{jobTitle}</p>}
          {enriched && email && (
            <div className={styles.emailWrapper}>
              <p className={styles.email}>{email}</p>
              {emailVerified && (
                <IconVerified
                  size={20}
                  color="var(--color-semantic-success)"
                  className={styles.verifiedIcon}
                />
              )}
              <button
                type="button"
                className={styles.copyButton}
                onClick={handleCopyEmail}
                aria-label={copied ? 'Copied' : 'Copy email'}
              >
                {copied ? (
                  <IconCheck size={14} color="var(--color-semantic-success)" />
                ) : (
                  <IconContentCopy size={14} color="var(--color-text-tertiary)" />
                )}
              </button>
            </div>
          )}
        </div>
        {enriched && (hasLinkedIn || transferLinkUrl || onViewJson || onDeleteContact) && (
          <div className={styles.actionIcons}>
            {hasLinkedIn && (
              <IconButton
                variant="tertiary"
                size="medium"
                aria-label="View LinkedIn profile"
                onClick={handleLinkedInClick}
              >
                <SocialIcon provider="linkedin" />
              </IconButton>
            )}
            {transferLinkUrl && (
              <Tooltip text={linkCopied ? 'Copied' : 'Copy transfer link'}>
                <IconButton
                  variant="tertiary"
                  size="medium"
                  aria-label={linkCopied ? 'Copied' : 'Copy transfer link'}
                  onClick={handleCopyTransferLink}
                >
                  {linkCopied ? (
                    <IconCheck color="var(--color-semantic-success)" />
                  ) : (
                    <IconLink color="var(--color-text-tertiary)" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {onViewJson && (
              <IconButton
                variant="tertiary"
                size="medium"
                aria-label="View enrichment data"
                onClick={onViewJson}
              >
                <IconInfo color="var(--color-text-tertiary)" />
              </IconButton>
            )}
            {onDeleteContact && (
              <IconButton
                variant="tertiary"
                size="small"
                aria-label={t('common.delete')}
                onClick={onDeleteContact}
                className={styles.deleteButton}
              >
                <IconTrash size={14} color="var(--color-text-tertiary)" />
              </IconButton>
            )}
          </div>
        )}
      </div>

      {/* Footer Section */}
      <div className={styles.footer}>
        {!enriched && !contacted && (
          <button
            type="button"
            className={styles.actionButton}
            onClick={onEnrich}
            disabled={!onEnrich}
          >
            <IconLightning size={20} color="var(--color-text-primary)" />
            <span>{!onEnrich ? 'Enriching...' : 'Enrich'}</span>
          </button>
        )}

        {enriched && !contacted && contactId === undefined && (
          <button
            type="button"
            className={styles.actionButton}
            onClick={onAddToContacts}
            disabled={!onAddToContacts}
          >
            <IconPaperplane size={20} color="var(--color-text-primary)" />
            <span>Add to Contacts</span>
          </button>
        )}

        {enriched && !contacted && contactId === null && (
          <button type="button" className={styles.actionButton} disabled>
            <IconLoading size={20} color="var(--color-text-primary)" />
            <span>Adding Contact...</span>
          </button>
        )}

        {enriched && !contacted && contactId !== undefined && contactId !== null && (
          <div className={styles.reachOutWrapper}>
            {reachOutPending ? (
              <button type="button" className={styles.actionButton} disabled>
                <IconLoading size={20} color="var(--color-text-primary)" />
                <span>{t('reachOut.sending')}</span>
              </button>
            ) : reachOutResult === 'success' ? (
              <button type="button" className={styles.actionButton} disabled>
                <IconCheckCircle size={20} color="var(--color-semantic-success)" />
                <span>{t('reachOut.success')}</span>
              </button>
            ) : (
              <>
                <Tooltip text={reachOutMessage?.replaceAll('_', ' ') || ''} position="bottom">
                <button
                  ref={reachOutButtonRef}
                  type="button"
                  className={`${styles.actionButton} ${
                    reachOutResult === 'failed' || reachOutResult === 'skipped'
                      ? styles.actionButtonError
                      : ''
                  }`}
                  onClick={reachOutResult === 'failed' || reachOutResult === 'skipped' ? undefined : handleReachOutClick}
                  disabled={reachOutResult === 'failed' || reachOutResult === 'skipped'}
                >
                  <IconPaperplane
                    size={20}
                    color={
                      reachOutResult === 'failed' || reachOutResult === 'skipped'
                        ? 'var(--color-semantic-danger)'
                        : 'var(--color-text-primary)'
                    }
                  />
                  <span>
                    {reachOutResult === 'failed'
                      ? t('reachOut.failed')
                      : reachOutResult === 'skipped'
                        ? t('reachOut.skipped')
                        : t('reachOut.reachOut')}
                  </span>
                </button>
                </Tooltip>

                {/* Sequence Picker Popover */}
                {showSequencePopover && hasSequenceFlow && (
                  <div ref={sequencePopoverRef} className={`${styles.sequencePopover} ${seqPopoverPlacement === 'below' ? styles.sequencePopoverBelow : ''}`}>
                    <div className={styles.popoverHeader}>
                      <span className={styles.popoverTitle}>
                        {t('reachOut.title')}
                      </span>
                      <button
                        type="button"
                        className={styles.closeButton}
                        onClick={() => {
                          setShowSequencePopover(false);
                          setSelectedSequence(null);
                        }}
                        aria-label="Close"
                      >
                        <IconClose size={12} color="var(--color-text-secondary)" />
                      </button>
                    </div>
                    <MenuSelect
                      items={sequences!.map((seq) => ({
                        value: seq.sequence_id,
                        label: seq.name,
                      }))}
                      value={selectedSequence}
                      onChange={setSelectedSequence}
                      maxHeight={180}
                    />
                    <div className={styles.popoverActions}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                          setShowSequencePopover(false);
                          setSelectedSequence(null);
                        }}
                      >
                        {t('reachOut.cancel')}
                      </Button>
                      <Button
                        variant="primary"
                        size="small"
                        disabled={!selectedSequence}
                        onClick={handleSequenceConfirm}
                      >
                        {t('reachOut.confirm')}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {contacted && (
          <div className={styles.statusWrapper}>
            <button
              ref={statusButtonRef}
              type="button"
              className={styles.statusButton}
              onClick={handleStatusClick}
              style={{ color: getStatusColor() }}
            >
              {getStatusIcon()}
              <span>{getStatusLabel()}</span>
              {contactId && (
                <Tooltip text="Open in Apollo" position="bottom">
                  <a
                    href={`https://app.apollo.io/#/contacts/${contactId}/activities`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.apolloLink}
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Open in Apollo"
                  >
                    <IconOpenInNew size={14} color={getStatusColor()} />
                  </a>
                </Tooltip>
              )}
            </button>

            {/* Sequence Log Popover (portal) */}
            {showPopover &&
              sequenceLog.length > 0 &&
              popoverCoords &&
              createPortal(
                <div
                  ref={popoverRef}
                  className={styles.popover}
                  style={{
                    top: popoverCoords.top,
                    right: document.documentElement.clientWidth - popoverCoords.left,
                  }}
                >
                  <div className={styles.popoverHeader}>
                    <div className={styles.popoverTitle}>
                      <IconLog size={16} color="var(--color-text-secondary)" />
                      <span>Sequence Log</span>
                      {contactId && (
                        <Tooltip text="Open in Apollo" position="bottom">
                          <a
                            href={`https://app.apollo.io/#/contacts/${contactId}/activities`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.apolloLink}
                            onClick={(e) => e.stopPropagation()}
                            aria-label="Open in Apollo"
                          >
                            <IconOpenInNew size={14} color="var(--color-text-secondary)" />
                          </a>
                        </Tooltip>
                      )}
                    </div>
                    <button
                      type="button"
                      className={styles.closeButton}
                      onClick={() => setShowPopover(false)}
                      aria-label="Close"
                    >
                      <IconClose size={12} color="var(--color-text-secondary)" />
                    </button>
                  </div>
                  <div className={styles.popoverTable}>
                    <div className={styles.tableHeader}>
                      <span className={styles.headerCell}>Send Time</span>
                      <span className={styles.headerCell}>Status</span>
                      <span className={styles.headerCell}>Subject</span>
                    </div>
                    <div className={styles.tableBody}>
                      {sequenceLog.map((entry, index) => (
                        <div key={index} className={styles.tableRow}>
                          <span className={styles.rowCell}>{entry.sendTime}</span>
                          <span className={styles.rowCell}>{entry.status}</span>
                          <Tooltip text={entry.subject} position="top" className={styles.subjectCell}>
                            <span className={styles.subjectText}>{entry.subject}</span>
                          </Tooltip>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>,
                document.body
              )}
          </div>
        )}
      </div>
    </div>
  );
};
